import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

interface Country { id: number; iso2: string; name: string; dial_code: string; }
interface BUser { id: number; username: string; display_name?: string; }
interface Bundle {
  id: number; tenant_id: number; name: string; description: string;
  included_minutes: number; max_concurrent: number | null;
  allowed_country_ids: number[]; users: BUser[];
}
interface PUser { id: number; username: string; tenant_id: number; bundle_id: number | null; extension_number: string | null; }
interface Data { countries: Country[]; bundles: Bundle[]; users: PUser[]; }

const flag = (iso: string) => (iso || '').replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

export default function BundlesPage(_props: { user?: any }) {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(''); const [msg, setMsg] = useState('');
  const [tab, setTab] = useState<'bundles' | 'assign'>('bundles');
  const [editId, setEditId] = useState<number | 'new' | null>(null);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  // editor state
  const [name, setName] = useState(''); const [desc, setDesc] = useState('');
  const [minutes, setMinutes] = useState('0'); const [conc, setConc] = useState('0');
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [assignBundle, setAssignBundle] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { const r = await axios.get('/api/bundles'); setData(r.data); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEditor = (b: Bundle | null) => {
    setMsg(''); setErr('');
    if (b) { setEditId(b.id); setName(b.name); setDesc(b.description || ''); setMinutes(String(b.included_minutes || 0)); setConc(String(b.max_concurrent || 0)); setSel(new Set(b.allowed_country_ids)); }
    else { setEditId('new'); setName(''); setDesc(''); setMinutes('0'); setConc('0'); setSel(new Set()); }
  };
  const toggle = (cid: number) => { const s = new Set(Array.from(sel)); s.has(cid) ? s.delete(cid) : s.add(cid); setSel(s); };

  const save = async () => {
    setSaving(true); setMsg(''); setErr('');
    const body = { name, description: desc, included_minutes: Number(minutes) || 0, max_concurrent: Number(conc) || 0, allowed_country_ids: Array.from(sel) };
    try {
      if (editId === 'new') await axios.post('/api/bundles', body);
      else await axios.put('/api/bundles/' + editId, body);
      setMsg('Bundle saved.'); setEditId(null); await load();
    } catch (e: any) { setErr(e?.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };
  const remove = async (b: Bundle) => {
    if (!window.confirm(`Delete bundle “${b.name}”?`)) return;
    setMsg(''); setErr('');
    try { await axios.delete('/api/bundles/' + b.id); setMsg('Bundle deleted.'); await load(); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Delete failed'); }
  };
  const doAssign = async (userId: number) => {
    if (assignBundle == null) return; setMsg(''); setErr('');
    try { await axios.post(`/api/bundles/${assignBundle}/assign`, { user_id: userId }); setMsg('Bundle assigned — limits and countries applied.'); await load(); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Assign failed'); }
  };
  const doUnassign = async (userId: number, bundleId: number) => {
    setMsg(''); setErr('');
    try { await axios.post(`/api/bundles/${bundleId}/unassign`, { user_id: userId }); setMsg('Bundle removed — user follows the organisation policy again.'); await load(); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Remove failed'); }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!data) return <div style={{ padding: 24, color: '#b00' }}>{err || 'No data'}</div>;

  const match = (c: Country) => c.name.toLowerCase().includes(q.toLowerCase()) || c.dial_code.includes(q) || c.iso2.toLowerCase().includes(q.toLowerCase());
  const countries = data.countries.filter(match);
  const bundleById: Record<number, Bundle> = {}; data.bundles.forEach((b) => { bundleById[b.id] = b; });

  const wrap: React.CSSProperties = { padding: 24, maxWidth: 1040 };
  const tabBtn = (a: boolean): React.CSSProperties => ({ padding: '8px 16px', border: 'none', borderBottom: a ? '3px solid #6366f1' : '3px solid transparent', background: 'none', fontWeight: 600, cursor: 'pointer', color: a ? '#6366f1' : '#555' });
  const chip = (on: boolean): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid ' + (on ? '#6366f1' : '#ddd'), borderRadius: 8, background: on ? '#eef0ff' : '#fff', cursor: 'pointer', fontSize: 14 });
  const btn: React.CSSProperties = { padding: '10px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
  const btnGhost: React.CSSProperties = { padding: '8px 14px', background: '#fff', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
  const btnDanger: React.CSSProperties = { padding: '8px 14px', background: '#fff', color: '#b00', border: '1px solid #e99', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
  const input: React.CSSProperties = { padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 14 };
  const card: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, background: '#fff' };

  return (
    <div style={wrap}>
      <h2 style={{ margin: '0 0 4px' }}>Calling Bundles</h2>
      <p style={{ color: '#666', marginTop: 0 }}>
        A bundle packages monthly minutes, a simultaneous-call limit and an allowed-country list.
        Assign a bundle to a user and all of it is enforced automatically; remove it and the user
        follows the organisation policy again.
      </p>
      {err && <div style={{ background: '#fdecea', color: '#b00', padding: 10, borderRadius: 8, marginBottom: 10 }}>{err}</div>}
      {msg && <div style={{ background: '#e8f5e9', color: '#1b5e20', padding: 10, borderRadius: 8, marginBottom: 10 }}>{msg}</div>}

      <div style={{ borderBottom: '1px solid #eee', marginBottom: 16 }}>
        <button style={tabBtn(tab === 'bundles')} onClick={() => { setTab('bundles'); setMsg(''); }}>Bundles</button>
        <button style={tabBtn(tab === 'assign')} onClick={() => { setTab('assign'); setEditId(null); setMsg(''); }}>User assignments</button>
      </div>

      {tab === 'bundles' && editId === null && (
        <div>
          <div style={{ marginBottom: 14 }}><button style={btn} onClick={() => openEditor(null)}>+ New bundle</button></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 12 }}>
            {data.bundles.length === 0 && <div style={{ color: '#999' }}>No bundles yet — create the first one.</div>}
            {data.bundles.map((b) => (
              <div key={b.id} style={card}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{b.name}</div>
                {b.description && <div style={{ color: '#666', fontSize: 13, marginTop: 2 }}>{b.description}</div>}
                <div style={{ margin: '10px 0', fontSize: 13, color: '#444', lineHeight: 1.7 }}>
                  <div>Minutes per month: <b>{b.included_minutes > 0 ? b.included_minutes : 'Unlimited'}</b></div>
                  <div>Simultaneous calls: <b>{b.max_concurrent && b.max_concurrent > 0 ? b.max_concurrent : 'Unlimited'}</b></div>
                  <div>Allowed countries: <b>{b.allowed_country_ids.length}</b></div>
                  <div>Assigned users: <b>{b.users.length}</b>{b.users.length > 0 && <span style={{ color: '#888' }}> — {b.users.map((u) => u.username).join(', ')}</span>}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={btnGhost} onClick={() => openEditor(b)}>Edit</button>
                  <button style={btnDanger} onClick={() => remove(b)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'bundles' && editId !== null && (
        <div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <div><div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Bundle name</div>
              <input style={{ ...input, width: 240 }} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. USA Calling — 1500 Minutes" /></div>
            <div><div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Description (optional)</div>
              <input style={{ ...input, width: 280 }} value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
            <div><div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Minutes / month (0 = unlimited)</div>
              <input style={{ ...input, width: 150 }} type="number" min={0} value={minutes} onChange={(e) => setMinutes(e.target.value)} /></div>
            <div><div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Simultaneous calls (0 = unlimited)</div>
              <input style={{ ...input, width: 150 }} type="number" min={0} value={conc} onChange={(e) => setConc(e.target.value)} /></div>
          </div>
          <div style={{ margin: '8px 0', fontWeight: 600 }}>Allowed countries <span style={{ color: '#666', fontWeight: 400 }}>({sel.size} selected — every other country is blocked for users on this bundle)</span></div>
          <input placeholder="Search country or dial code…" value={q} onChange={(e) => setQ(e.target.value)}
            style={{ ...input, width: '100%', maxWidth: 340, marginBottom: 12 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 8, maxHeight: 420, overflowY: 'auto', paddingRight: 6 }}>
            {countries.map((c) => {
              const on = sel.has(c.id);
              return (
                <div key={c.id} style={chip(on)} onClick={() => toggle(c.id)}>
                  <input type="checkbox" checked={on} readOnly />
                  <span>{flag(c.iso2)} {c.name}</span>
                  <span style={{ marginLeft: 'auto', color: '#999' }}>+{c.dial_code}</span>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
            <button style={{ ...btn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>{saving ? 'Saving…' : (editId === 'new' ? 'Create bundle' : 'Save changes')}</button>
            <button style={btnGhost} onClick={() => setEditId(null)}>Cancel</button>
          </div>
          {editId !== 'new' && <p style={{ color: '#996a00', marginTop: 12, fontSize: 13 }}>Saving changes re-applies this bundle to every assigned user immediately.</p>}
        </div>
      )}

      {tab === 'assign' && (
        <div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Bundle to assign</div>
            <select style={{ ...input, minWidth: 280 }} value={assignBundle ?? ''} onChange={(e) => setAssignBundle(e.target.value ? Number(e.target.value) : null)}>
              <option value="">— select a bundle —</option>
              {data.bundles.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ textAlign: 'left', color: '#666' }}>
              <th style={{ padding: '8px 10px', borderBottom: '1px solid #eee' }}>User</th>
              <th style={{ padding: '8px 10px', borderBottom: '1px solid #eee' }}>Extension</th>
              <th style={{ padding: '8px 10px', borderBottom: '1px solid #eee' }}>Current bundle</th>
              <th style={{ padding: '8px 10px', borderBottom: '1px solid #eee' }}></th>
            </tr></thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id}>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f5f5f5' }}>{u.username}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f5f5f5' }}>{u.extension_number || '—'}</td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f5f5f5' }}>
                    {u.bundle_id && bundleById[u.bundle_id] ? <span style={{ color: '#6366f1', fontWeight: 600 }}>{bundleById[u.bundle_id].name}</span> : <span style={{ color: '#999' }}>None</span>}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: '1px solid #f5f5f5', textAlign: 'right' }}>
                    {u.bundle_id ? (
                      <button style={btnDanger} onClick={() => doUnassign(u.id, u.bundle_id!)}>Remove bundle</button>
                    ) : (
                      <button style={{ ...btnGhost, opacity: assignBundle == null ? 0.5 : 1 }} disabled={assignBundle == null} onClick={() => doAssign(u.id)}>Assign selected</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
